import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import { api } from "../lib/api";
import { SOURCE_LABEL, errorMessage, fileToBase64 } from "../lib/renterDrivers";
import DriverDetailsForm, { emptyDetails, toPayload } from "../components/renter-drivers/DriverDetailsForm";
import NoIndex from "../components/NoIndex";

// Task 6: the invited driver's private page. It shows only what this person
// needs: the business, vehicle, dates, requirements and privacy notice. No
// price, renter profile or other drivers.
export default function DriverInvitation() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [details, setDetails] = useState(emptyDetails());
  const [ack, setAck] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const load = useCallback(() => {
    api.get(`/public/driver-invitations/${token}`).then((r) => {
      setInfo(r.data);
      if (r.data.your_details) setSubmitted(true);
      else setDetails((d) => ({ ...d, legal_name: d.legal_name || r.data.your_name || "" }));
    }).catch((err) => setError(errorMessage(err, "This invitation link is no longer active.")));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  if (error) return <main className="max-w-lg mx-auto p-4"><NoIndex /><p className="rounded-xl border bg-white p-5 text-slate-700" data-testid="invitation-error">{error}</p></main>;
  if (!info) return <main className="max-w-lg mx-auto p-4 text-slate-500">Loading…</main>;

  const submit = async () => {
    try {
      await api.post(`/public/driver-invitations/${token}/details`, { details: toPayload(details), privacy_acknowledged: ack });
      toast.success("Thank you. Now upload a photo of your licence.");
      setSubmitted(true);
      load();
    } catch (err) { toast.error(errorMessage(err)); }
  };
  const upload = async (file) => {
    if (!file) return;
    try {
      await api.post(`/public/driver-invitations/${token}/evidence`, {
        kind: "LICENCE_FRONT", data_base64: await fileToBase64(file), content_type: file.type || "application/pdf",
        filename: file.name, idempotency_key: `${token.slice(0, 12)}-${file.name}-${file.size}` });
      toast.success("Uploaded. The rental business will review it.");
    } catch (err) { toast.error(errorMessage(err)); }
  };

  return (
    <main className="max-w-lg mx-auto px-4 py-6 space-y-4" data-testid="driver-invitation">
      <NoIndex />
      <h1 className="font-display text-2xl font-bold">Driver details for {info.business_name}</h1>
      <p className="text-sm text-slate-700">You were added as {info.role === "PRIMARY" ? "the primary" : "an additional"} driver for
        {" "}{info.vehicle?.name} from {new Date(info.pickup_date).toLocaleDateString()} to {new Date(info.return_date).toLocaleDateString()}.</p>
      <p className="text-sm text-slate-700">{info.unauthorized_driver_rule}</p>
      {info.requirements?.length > 0 && (
        <ul className="rounded-lg border bg-white p-3 text-sm space-y-0.5">
          {info.requirements.map((r) => <li key={r.key}>{r.text} <span className="text-xs text-slate-500">— {SOURCE_LABEL[r.source]}</span></li>)}
        </ul>
      )}
      {!submitted ? (
        <section className="rounded-xl border bg-white p-4 space-y-3">
          <DriverDetailsForm value={details} onChange={setDetails} idPrefix="invite" />
          <label className="flex items-start gap-2 text-xs"><input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="invite-privacy" />
            <span>{info.privacy_notice}</span></label>
          <button disabled={!ack || !details.legal_name.trim()} onClick={submit} data-testid="invite-submit"
                  className="w-full px-4 py-2.5 rounded-lg btn-brand disabled:opacity-50">Submit my details</button>
        </section>
      ) : (
        <section className="rounded-xl border bg-white p-4 space-y-2" data-testid="invite-submitted">
          <p className="text-sm">Your details were received. They are not verified until the rental business reviews them.</p>
          <label className="block text-sm underline cursor-pointer">Upload a photo of your licence
            <input type="file" className="sr-only" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={(e) => upload(e.target.files?.[0])} /></label>
          <p className="text-xs text-slate-500">Bring your original licence to pickup.</p>
        </section>
      )}
    </main>
  );
}
